import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useCountyApi } from '../../services/api/useCounty.api';
import { useTeamApi } from '../../services/api/useTeam.api';
import { useTeamTypeApi } from '../../services/api/useTeamType.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { Team } from '../../types/Team/Team.type';
import { TeamUpdate } from '../../types/Team/TeamUpdate.type';
import TeamRow from './TeamRow';

interface TeamsTableProps {
  snackbarData: (message: string, severity: 'success' | 'error') => void;
}

function TeamsTable({ snackbarData }: TeamsTableProps) {
  const queryClient = useQueryClient();
  const { getTeams, updateTeam, deleteTeam } = useTeamApi();
  const { getCounties } = useCountyApi();
  const { getTeamTypes } = useTeamTypeApi();

  const { ref: teamsRef, inView: teamsInView } = useInView();

  const {
    data: infiniteTeams,
    isLoading: teamsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['allTeams'],
    queryFn: async ({ pageParam = 1 }) => {
      return await getTeams({ page: pageParam, per_page: 10, sort: 'name:asc' });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Team[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const teams = infiniteTeams?.pages?.flatMap((page) => page.data) ?? [];

  const { data: counties } = useQuery({
    queryKey: ['counties'],
    queryFn: () => getCounties({ is_active: true }),
  });

  const { data: teamTypes } = useQuery({
    queryKey: ['teamTypes'],
    queryFn: getTeamTypes,
  });

  const updateMutation = useMutation({
    mutationFn: ({ teamId, payload }: { teamId: number; payload: TeamUpdate }) => updateTeam(teamId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allTeams'] });
      snackbarData('Team updated successfully!', 'success');
    },
    onError: (error) => {
      snackbarData(error?.message || 'Failed to update team. Please try again.', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (teamId: number) => deleteTeam(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allTeams'] });
      snackbarData('Team deleted successfully!', 'success');
    },
    onError: (error) => {
      snackbarData(error?.message || 'Failed to delete team. Please try again.', 'error');
    },
  });

  const handleUpdate = (
    team: Team,
    newName: string,
    newCountyId: number,
    newTeamTypeId: number,
    newDatasetIds: number[],
  ) => {
    const payload: TeamUpdate = {};

    if (newName !== team.name) {
      payload.name = newName;
    }
    if (newCountyId !== team.county.id) {
      payload.county_id = newCountyId;
    }
    if (newTeamTypeId !== team.team_type.id) {
      payload.team_type_id = newTeamTypeId;
    }

    const currentDatasetIds = team.datasets?.map((dataset) => dataset.id) ?? [];
    const sortedCurrent = [...currentDatasetIds].sort((a, b) => a - b);
    const sortedNew = [...newDatasetIds].sort((a, b) => a - b);
    const sameDatasets =
      sortedCurrent.length === sortedNew.length && sortedCurrent.every((id, index) => id === sortedNew[index]);
    if (!sameDatasets) {
      payload.dataset_ids = newDatasetIds;
    }

    updateMutation.mutate({ teamId: team.id, payload });
  };

  const handleDelete = (teamId: number) => {
    deleteMutation.mutate(teamId);
  };

  useEffect(() => {
    if (teamsInView && fetchNextPage && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [teamsInView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const isLoading = teamsLoading;
  const isMutating = updateMutation.isPending || deleteMutation.isPending;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ border: '1px solid #cbd5e0', boxShadow: 'none', maxHeight: 400 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Team Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>County</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Datasets</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Team Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teams.map((team: Team) => (
            <TeamRow
              key={team.id}
              team={team}
              counties={counties || []}
              teamTypes={teamTypes || []}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              isLoading={isMutating}
            />
          ))}
          <TableRow ref={teamsRef}>
            <TableCell colSpan={5} sx={{ border: 'none', padding: 1 }}>
              {isFetchingNextPage && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TeamsTable;
